import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Loader2, Trash2 } from 'lucide-react';
import AcceleraLogo from '../assets/images/accelera.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

//set push the code
const ChatInterface = () => {
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: "Hi! I’m your Accelera Assistant.  I can help with any questions you have about the new Proprio system, ordering or help if you are calling about your existing device.  How can I help you today?" 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    initializeChat();
  }, []);

  const initializeChat = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/start`);
      const data = await response.json();
      setThreadId(data.thread_id);
    } catch (error) {
      console.error('Error initializing chat:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble connecting. Please try again.'
      }]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !threadId) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: input,
          thread_id: threadId
        }),
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || 'Message received. Responding to your request....'
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content:
          "Hi! I’m your Accelera Assistant.  I can help with any questions you have about the new Proprio system, ordering or help if you are calling about your existing device.  How can I help you today?",
          
      },
    ]);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-r from-blue-50 to-blue-100">
      {/* The entire Header section will be removed */}
      {/* Header */}

      {/* Chat Container */}
      <div className="flex-1 max-w-md w-full mx-auto p-6">
        <div className="bg-white shadow-lg rounded-lg flex flex-col h-full">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 ${
                  message.role === 'user' ? 'flex-row-reverse' : ''
                } animate-fade-in`}
                style={{ animationDelay: `${index * 60}ms` }}
                tabIndex={0}
                aria-label={message.role === 'user' ? 'User message' : 'Assistant message'}
              >
                <div
                  className={`w-10 h-10 flex items-center justify-center ${
                    message.role === 'user' ? 'rounded-full bg-blue-500' : ''
                  }`}
                >
                  {message.role === 'user' ? (
                    <User className="w-6 h-6 text-white" />
                  ) : (
                    <img src={AcceleraLogo} alt="Accelera Assistant" className="w-8 h-8" /> 
                  )}
                </div>
                <div
                  className={`rounded-2xl px-4 py-2 shadow-sm max-w-[75%]\
                    ${message.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                >
                  <p className="text-base whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500 animate-pulse">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Responding to your request...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="p-4 bg-gray-50 border-t rounded-b-lg">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="w-0 flex-1 p-3 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Type your message"
                autoFocus
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading || !threadId}
                className="p-3 rounded-full bg-blue-500 text-white shadow hover:bg-blue-600 disabled:opacity-50 transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Send message"
              >
                <Send className="w-6 h-6" />
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease both;
        }
      `}</style>
    </div>
  );
};

export default ChatInterface;
